import { 
  AggregateOperationEnum,
  AggregateQueryOptions,
  AggregateClause,
  Query,
  SortDirectionEnum
} from '@/lib/engine';
import { toast } from '@/components/ui';
import { getValidAggregationsForColumn } from './types';
import { ColumnRef, OrderByClause, SortDirection } from '../../data-grid/types';

// Action types
export enum AggregateActionType {
  TOGGLE_GROUP_BY = 'TOGGLE_GROUP_BY',
  TOGGLE_AGGREGATE = 'TOGGLE_AGGREGATE',
  UPDATE_AGGREGATE_ALIAS = 'UPDATE_AGGREGATE_ALIAS',
  REMOVE_AGGREGATE = 'REMOVE_AGGREGATE',
  UPDATE_WHERE_CLAUSE = 'UPDATE_WHERE_CLAUSE',
  UPDATE_HAVING_CLAUSE = 'UPDATE_HAVING_CLAUSE',
  ADD_ORDER_BY = 'ADD_ORDER_BY',
  REMOVE_ORDER_BY = 'REMOVE_ORDER_BY',
  UPDATE_ORDER_BY_DIRECTION = 'UPDATE_ORDER_BY_DIRECTION',
}

// Action interfaces
interface ToggleGroupByAction {
  type: AggregateActionType.TOGGLE_GROUP_BY;
  column: any;
}

interface ToggleAggregateAction {
  type: AggregateActionType.TOGGLE_AGGREGATE;
  column: any;
  operation: AggregateOperationEnum;
}

interface UpdateAggregateAliasAction {
  type: AggregateActionType.UPDATE_AGGREGATE_ALIAS;
  index: number;
  alias: string;
}

interface RemoveAggregateAction {
  type: AggregateActionType.REMOVE_AGGREGATE;
  index: number;
}

interface UpdateWhereClauseAction {
  type: AggregateActionType.UPDATE_WHERE_CLAUSE;
  where: string;
}

interface UpdateHavingClauseAction {
  type: AggregateActionType.UPDATE_HAVING_CLAUSE;
  having: string;
}

interface AddOrderByAction {
  type: AggregateActionType.ADD_ORDER_BY;
  column: ColumnRef;
}

interface RemoveOrderByAction {
  type: AggregateActionType.REMOVE_ORDER_BY;
  index: number;
}

interface UpdateOrderByDirectionAction {
  type: AggregateActionType.UPDATE_ORDER_BY_DIRECTION;
  index: number;
  direction: SortDirection;
}

// Union type for all actions
export type AggregateAction = 
  | ToggleGroupByAction
  | ToggleAggregateAction
  | UpdateAggregateAliasAction
  | RemoveAggregateAction
  | UpdateWhereClauseAction
  | UpdateHavingClauseAction
  | AddOrderByAction
  | RemoveOrderByAction
  | UpdateOrderByDirectionAction;

// Helper functions
export function isColumnInGroupBy(options: AggregateQueryOptions, column: any): boolean {
  return options.groupBy.some(c => c.name === column.name);
}

export function hasAggregateOperation(options: AggregateQueryOptions, column: any, operation: AggregateOperationEnum): boolean {
  return options.aggregates.some(
    agg => agg.column.name === column.name && agg.operation === operation
  );
}

function checkHavingClauseForRemovedColumns(
  having: string | undefined, 
  removedColumnNames: string[]
): { having: string | undefined, wasCleared: boolean } {
  if (!having) return { having, wasCleared: false };
  
  const havingContainsRemovedColumn = removedColumnNames.some(name => 
    having.includes(name)
  );
  
  if (havingContainsRemovedColumn) {
    return { having: '', wasCleared: true };
  }
  
  return { having, wasCleared: false };
}

// Action creators
export const aggregateActions = {
  toggleGroupBy: (column: any): ToggleGroupByAction => ({
    type: AggregateActionType.TOGGLE_GROUP_BY,
    column
  }),
  
  toggleAggregate: (column: any, operation: AggregateOperationEnum): ToggleAggregateAction => ({
    type: AggregateActionType.TOGGLE_AGGREGATE,
    column,
    operation
  }),
  
  updateAggregateAlias: (index: number, alias: string): UpdateAggregateAliasAction => ({
    type: AggregateActionType.UPDATE_AGGREGATE_ALIAS,
    index,
    alias
  }),
  
  removeAggregate: (index: number): RemoveAggregateAction => ({
    type: AggregateActionType.REMOVE_AGGREGATE,
    index
  }),
  
  updateWhereClause: (where: string): UpdateWhereClauseAction => ({
    type: AggregateActionType.UPDATE_WHERE_CLAUSE,
    where
  }),
  
  updateHavingClause: (having: string): UpdateHavingClauseAction => ({
    type: AggregateActionType.UPDATE_HAVING_CLAUSE,
    having
  }),
  
  addOrderBy: (column: ColumnRef): AddOrderByAction => ({
    type: AggregateActionType.ADD_ORDER_BY,
    column
  }),
  
  removeOrderBy: (index: number): RemoveOrderByAction => ({
    type: AggregateActionType.REMOVE_ORDER_BY,
    index
  }),
  
  updateOrderByDirection: (index: number, direction: SortDirection): UpdateOrderByDirectionAction => ({
    type: AggregateActionType.UPDATE_ORDER_BY_DIRECTION,
    index,
    direction
  })
};

// Reducer function
export function aggregateReducer(query: Query, action: AggregateAction): Query {
  const options = query.options as AggregateQueryOptions;
  
  switch (action.type) {
    case AggregateActionType.TOGGLE_GROUP_BY: {
      const isGrouped = isColumnInGroupBy(options, action.column);
      let groupBy = [...options.groupBy];
      let aggregates = [...options.aggregates];
      let having = options.having;
      
      if (isGrouped) {
        // Remove from groupBy
        groupBy = groupBy.filter(c => c.name !== action.column.name);
        
        // Check if the removed GROUP BY column is used in the HAVING clause
        const { having: newHaving, wasCleared } = checkHavingClauseForRemovedColumns(
          having, 
          [action.column.name]
        );
        
        having = newHaving;
        
        if (wasCleared) {
          toast.info('HAVING clause was cleared because it referenced removed GROUP BY columns', {
            duration: 5000,
            closeButton: true,
          });
        }
      } else {
        // Add to groupBy
        groupBy = [...groupBy, action.column];
        
        // Remove any aggregates for this column when it's grouped
        const hasAggregations = aggregates.some(
          agg => agg.column.name === action.column.name
        );
        
        if (hasAggregations) {
          const removedAggregates = aggregates.filter(
            agg => agg.column.name === action.column.name
          );
          
          aggregates = aggregates.filter(
            agg => agg.column.name !== action.column.name
          );
          
          // Check if any removed aggregates are used in the HAVING clause
          const removedAliases = removedAggregates.map(agg => agg.alias);
          const { having: newHaving, wasCleared } = checkHavingClauseForRemovedColumns(
            having, 
            removedAliases
          );
          
          having = newHaving;
          
          if (wasCleared) {
            toast.info('HAVING clause was cleared because it referenced removed aggregates', {
              duration: 5000,
              closeButton: true,
            });
          }
        }
      }
      
      return {
        ...query,
        options: {
          ...options,
          groupBy,
          aggregates,
          having
        }
      };
    }
    
    case AggregateActionType.TOGGLE_AGGREGATE: {
      const isActive = hasAggregateOperation(options, action.column, action.operation);
      let aggregates = [...options.aggregates];
      let groupBy = [...options.groupBy];
      let having = options.having;
      
      if (isActive) {
        // Remove this aggregate
        const removedAggregates = aggregates.filter(
          agg => agg.column.name === action.column.name && agg.operation === action.operation
        );
        
        aggregates = aggregates.filter(
          agg => !(agg.column.name === action.column.name && agg.operation === action.operation)
        );
        
        // Check if there are any other aggregations for this column
        const hasOtherAggregations = aggregates.some(
          agg => agg.column.name === action.column.name
        );
        
        // If no other aggregations for this column, add it to GROUP BY
        if (!hasOtherAggregations && !isColumnInGroupBy(options, action.column)) {
          groupBy = [...groupBy, action.column];
        }
        
        // Check if any removed aggregates are used in the HAVING clause
        const removedAliases = removedAggregates.map(agg => agg.alias);
        const { having: newHaving, wasCleared } = checkHavingClauseForRemovedColumns(
          having, 
          removedAliases
        );
        
        having = newHaving;
        
        if (wasCleared) {
          toast.info('HAVING clause was cleared because it referenced removed aggregates', {
            duration: 5000,
            closeButton: true,
          });
        }
      } else {
        // Check if the operation is valid for this column type
        const validOperations = getValidAggregationsForColumn(action.column);
        if (!validOperations.includes(action.operation)) {
          toast.error(`Operation ${action.operation} is not valid for column type ${action.column.schema.type.toString()}`, {
            description: 'Please select a different operation or column.',
            duration: 5000,
            closeButton: true,
          });
          return query;
        }
        
        // If column is in GROUP BY, remove it
        if (isColumnInGroupBy(options, action.column)) {
          groupBy = groupBy.filter(c => c.name !== action.column.name);
          
          // Check if the removed GROUP BY column is used in the HAVING clause
          const { having: newHaving, wasCleared } = checkHavingClauseForRemovedColumns(
            having, 
            [action.column.name]
          );
          
          having = newHaving;
          
          if (wasCleared) {
            toast.info('HAVING clause was cleared because it referenced removed GROUP BY columns', {
              duration: 5000,
              closeButton: true,
            });
          }
        }
        
        // Create a new aggregate
        const newAggregate: AggregateClause = {
          operation: action.operation,
          column: action.column,
          alias: `${action.operation.toLowerCase()}_${action.column.name}`
        };
        
        // Add it to the list
        aggregates = [...aggregates, newAggregate];
      }
      
      return {
        ...query,
        options: {
          ...options,
          aggregates,
          groupBy,
          having
        }
      };
    }
    
    case AggregateActionType.UPDATE_AGGREGATE_ALIAS: {
      const aggregates = [...options.aggregates];
      const oldAlias = aggregates[action.index]?.alias || '';
      
      // Update the alias
      if (aggregates[action.index]) {
        aggregates[action.index] = {
          ...aggregates[action.index],
          alias: action.alias
        } as AggregateClause;
      }
      
      // Check if the old alias is used in the HAVING clause
      let having = options.having;
      if (having && having.includes(oldAlias)) {
        // Replace all occurrences of the old alias with the new one
        having = having.replace(new RegExp(oldAlias, 'g'), action.alias);
      }
      
      return {
        ...query,
        options: {
          ...options,
          aggregates,
          having
        }
      };
    }
    
    case AggregateActionType.REMOVE_AGGREGATE: {
      const aggregates = [...options.aggregates];
      const removedAggregate = aggregates[action.index];
      
      // Skip if the index is invalid
      if (!removedAggregate) {
        return query;
      }
      
      // Remove the aggregate
      aggregates.splice(action.index, 1);
      
      // Check if there are any other aggregations for this column
      const hasOtherAggregations = aggregates.some(
        agg => agg.column.name === removedAggregate.column.name
      );
      
      // If no other aggregations for this column, add it to GROUP BY
      let groupBy = [...options.groupBy];
      if (!hasOtherAggregations && !isColumnInGroupBy(options, removedAggregate.column)) {
        groupBy = [...groupBy, removedAggregate.column];
      }
      
      // Check if the removed aggregate is used in the HAVING clause
      let having = options.having;
      const { having: newHaving, wasCleared } = checkHavingClauseForRemovedColumns(
        having, 
        [removedAggregate.alias]
      );
      
      having = newHaving;
      
      if (wasCleared) {
        toast.info('HAVING clause was cleared because it referenced removed aggregates', {
          duration: 5000,
          closeButton: true,
        });
      }
      
      return {
        ...query,
        options: {
          ...options,
          aggregates,
          groupBy,
          having
        }
      };
    }
    
    case AggregateActionType.UPDATE_WHERE_CLAUSE: {
      return {
        ...query,
        options: {
          ...options,
          where: action.where
        }
      };
    }
    
    case AggregateActionType.UPDATE_HAVING_CLAUSE: {
      return {
        ...query,
        options: {
          ...options,
          having: action.having
        }
      };
    }
    
    case AggregateActionType.ADD_ORDER_BY: {
      // Initialize orderBy if it doesn't exist
      const orderBy = options.orderBy || [];
      
      // Check if column is already in orderBy
      const existingIndex = orderBy.findIndex(item => 
        item.column && item.column.name === action.column.name
      );
      
      if (existingIndex >= 0) {
        // If already exists, toggle direction or remove
        const newOrderBy = [...orderBy];
        const orderByItem = newOrderBy[existingIndex];
        
        if (orderByItem && orderByItem.column) {
          const currentDirection = orderByItem.direction;
          
          if (currentDirection === SortDirectionEnum.ASC) {
            // Toggle from ASC to DESC
            newOrderBy[existingIndex] = {
              column: orderByItem.column,
              direction: SortDirectionEnum.DESC
            };
          } else {
            // If it's DESC, remove it (turn off)
            newOrderBy.splice(existingIndex, 1);
          }
          
          return {
            ...query,
            options: {
              ...options,
              orderBy: newOrderBy
            }
          };
        }
      }
      
      // Add new orderBy clause
      const newOrderByClause = {
        column: action.column,
        direction: SortDirectionEnum.ASC
      } as OrderByClause;
      
      return {
        ...query,
        options: {
          ...options,
          orderBy: [...orderBy, newOrderByClause]
        }
      };
    }
    
    case AggregateActionType.REMOVE_ORDER_BY: {
      const orderBy = options.orderBy || [];
      
      return {
        ...query,
        options: {
          ...options,
          orderBy: orderBy.filter((_, index) => index !== action.index)
        }
      };
    }
    
    case AggregateActionType.UPDATE_ORDER_BY_DIRECTION: {
      const orderBy = options.orderBy || [];
      
      return {
        ...query,
        options: {
          ...options,
          orderBy: orderBy.map((item, index) => 
            index === action.index
              ? { ...item, direction: action.direction }
              : item
          )
        }
      };
    }
    
    default:
      return query;
  }
}

// Custom hook to use the aggregate store
export function useAggregateStore(query: Query, updateQuery: (query: Query) => void) {
  // Dispatch function that applies the reducer and calls updateQuery
  const dispatch = (action: AggregateAction) => {
    try {
      const newQuery = aggregateReducer(query, action);
      updateQuery(newQuery);
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while updating query';
      
      toast.error(errorMessage, {
        description: 'Please try again.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error updating query:', error);
    }
  };
  
  return {
    dispatch,
    actions: aggregateActions
  };
}

/**
 * Get the available columns for the HAVING clause
 * This includes both GROUP BY columns and aggregated columns with their aliases
 */
export function getHavingColumns(groupByColumns: any[], aggregateColumns: AggregateClause[]) {
  // Start with the GROUP BY columns
  const havingColumns = [...groupByColumns];
  
  // Add the aggregated columns with their aliases
  const aggregateHavingColumns = aggregateColumns.map(agg => ({
    name: agg.alias,
    schema: agg.column.schema,
    // Add a reference to the original aggregate for tracking
    _sourceAggregate: {
      column: agg.column.name,
      operation: agg.operation
    }
  }));
  
  return [...havingColumns, ...aggregateHavingColumns];
} 